<template>
  <div v-loading="loading || proReportDataLoading" element-loading-spinner="atom-audit-loader" style="min-height: 200px;">
    <div v-for="group in allData" :key="group.key">
      <template v-if="!codeType || group.key === codeType">
        <h2>{{ group.key }}</h2>
        <by-code-calculations-help>
          <el-dropdown trigger="click" @command="handleTableCommands">
            <span class="el-dropdown-link">
              <i title="table actions" class="mdi mdi-dots-horizontal" style="font-size: 20px; cursor: pointer;"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item :command="buildTableCommands('download', `emTable_${group.key}`)">Download</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </by-code-calculations-help>
        <el-table :id="`emTable_${group.key}`" class="tableNoWrap" :ref="`emTable_${group.key}`" :data="group.data" empty-text="No EM Data Available" max-height="425" stripe :summary-method="getSummaries" :cell-class-name="cellClasses" show-summary row-key="code">
          <el-table-column prop="code" label="Code" width="100" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.code, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="Description" min-width="220" sortable>
            <template slot-scope="scope">
              <div tag="description" :title="scope.row.description" v-html="highlightValue(scope.row.description, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="totalRecords" label="Occurrences" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.totalRecords, filterCriteria)" title="View Account Numbers" class="details"></div>
            </template>
          </el-table-column>
          <el-table-column prop="emNoRec" label="No Changes" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.emNoRec, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="emDeletions" label="Deletions" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.emDeletions, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="revisedFromCount" label="Revised From" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.revisedFromCount, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="emCodes" label="Accuracy Rate" sortable>
            <template slot-scope="scope">
              <div v-if="scope.row.emCodes == null">N/A</div>
              <div v-else v-html="highlightValue(`${(scope.row.emCodes * 100).toFixed(2)}%`, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="emAdditions" label="Additions" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.emAdditions, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="revisedToCount" label="Revised To" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.revisedToCount, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="percentOfMissedOpportunities" label="% of All Missed Opportunities" sortable>
            <template slot-scope="scope">
              {{ formatPercentage(scope.row.percentOfMissedOpportunities) }}
            </template>
          </el-table-column>
          <el-table-column label="Details" width="120" prop="details" v-if="!hideDetails">
            <template slot-scope="scope">
              <el-button @click.native.prevent="updateAcctList(scope.row)" plain round size="mini" icon="mdi mdi-details" title="Records Included" circle> </el-button>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </div>

    <center v-if="!loading">
      <div class="no-data-message" v-if="allData.length === 0">No EM Data Available</div>
    </center>

    <el-dialog :visible.sync="dialogVisible" center destroy-on-close width="75%">
      <accountList :header="detailsHeader" v-if="dialogVisible" :proRecordIds="subProRecordIds"></accountList>
    </el-dialog>

    <!-- <el-dialog :visible.sync="summaryDialogVisible" center destroy-on-close>
      <IPSummary v-if="summaryDialogVisible" :item="summaryRow"></IPSummary>
    </el-dialog> -->
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import axios from '@/utilities/axios-global'
  import hiaFilters from '@/mixins/hiaFilters'
  import money from '@/mixins/money'
  import dashboardHelper from '@/mixins/dashboardHelper'
  import accountList from './accoutList'
  import ByCodeCalculationsHelp from './components/ByCodeCalculationsHelp'

  export default {
    name: 'PRO_EMSummary',
    mixins: [hiaFilters, money, dashboardHelper],
    props: ['proRecordIds', 'hideDetails', 'codeType', 'codeTypes'],
    components: {
      accountList,
      ByCodeCalculationsHelp
    },
    data: function() {
      return {
        allData: [],
        filterCriteria: '',
        loading: false,
        dialogVisible: false,
        summaryDialogVisible: false,
        subProRecordIds: null,
        summaryRow: {},
        detailsHeader: ''
      }
    },
    methods: {
      async getData() {
        const payload = {
          proRecordIds: this.proRecordIds,
          codeTypes: this.codeTypes,
          includeAccts: true,
          statType: 'test'
        }
        try {
          this.loading = true
          const response = await axios.post(this.baseApiUrl + '/reports/dashboard/proByEM', payload)
          this.allData = response.data.data
          if (this.codeType === 'All Code Types' && !this.allData.some(x => x.key === this.codeType)) {
            const codes = this.getAllCodeTypesPro(this.allData, 'accuracyRate')
            this.allData = [{ key: 'All Code Types', data: codes }]
          }
          if (this.codeType && !this.allData.some(x => x.key === this.codeType)) {
            this.allData.push({ key: this.codeType, data: [] })
          }
          this.attachMissedOpportunityPctToGroups(this.allData, 'emAdditions', 'revisedToCount')
        } catch (err) {
          this.allData = []
        } finally {
          this.loading = false
        }
      },
      updateAcctList(row) {
        this.detailsHeader = row.code
        this.subProRecordIds = row.accountNumbers
        this.dialogVisible = !this.dialogVisible
      },
      getSummaries(param) {
        return this.getSummary(param)
      },
      convertHccs(delimited) {
        if (delimited) {
          const retArray = delimited.split('-')
          return retArray
        } else {
          return []
        }
      },
      cellClasses({ row, column, rowIndex, columnIndex }) {
        if (column.property === 'code' || column.property === 'description' || column.property === 'codeDesc') {
          return 'textAlignLeft'
        }
        if (column.property === 'details') {
          return 'textAlignCenter'
        }
      },
      getAllCodeTypesPro(data, accuracyColKey) {
        let arr = []
        data.forEach(x => {
          arr = arr.concat(x.data)
        })
        const allCodeTypes = []
        arr.reduce((res, value) => {
          if (!res[value.code]) {
            res[value.code] = {
              code: value.code,
              hcc: value.hcc,
              description: value.description,
              cmCodes: 0,
              totalRecords: 0,
              emAdditions: 0,
              emDeletions: 0,
              emNoRec: 0,
              emRevisions: 0,
              revisedFromCount: 0,
              revisedToCount: 0
            }
            allCodeTypes.push(res[value.code])
          }
          const row = res[value.code]
          row.emAdditions += value.emAdditions
          row.emDeletions += value.emDeletions
          row.emNoRec += value.emNoRec
          row.emRevisions += value.emRevisions
          row.revisedFromCount += value.revisedFromCount || 0
          row.revisedToCount += value.revisedToCount || 0
          row.totalRecords += value.totalRecords

          const denominator = row.emNoRec + row.emDeletions + (row.revisedFromCount || 0)
          const rate = denominator ? row.emNoRec / denominator : null
          row[accuracyColKey] = rate
          row.emCodes = rate
          return res
        }, {})
        return allCodeTypes
      },
      handleTableCommands(command) {
        this.$emit('handleTableCommands', { command: command.command, ref: command.key, csvName: `${command.key}_table`, headers: [], excludeHeaders: ['Details'] })
      },
      buildTableCommands(command, key) {
        const commandObject = {
          command: command,
          key: key
        }
        return commandObject
      }
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      ...mapState('reports/', ['proReportDataLoading'])
    },
    mounted: async function() {
      await this.getData()
    },
    activated: async function() {
      await this.getData()
    },
    watch: {
      proRecordIds: async function() {
        await this.getData()
      },
      codeTypes: async function() {
        await this.getData()
      }
    }
  }
</script>

<style scoped></style>
